// ./app/components/Flow/Flow.tsx

'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  addEdge,
  Connection,
  updateEdge,
} from 'reactflow';

import { fitViewOptions, proOptions } from '#/config/constants';
import { useFlowContext } from '#/providers/FlowContext';
import { IWorkspace, RunStatus } from '#/types/workspace';

import FlowInterfaces from './FlowInterfaces';
import { edgeTypes } from './Nodes/TurboEdge';
import { nodeTypes } from './Nodes/TurboNode';

const defaultEdgeOptions = {
  type: 'turbo',
  markerEnd: 'edge-circle',
  animated: true,
};

function WorkspaceFlow(props: {
  workspaceUuid: string;
  workspace: IWorkspace;
  isLateral?: boolean;
}) {
  const { workspaceUuid, workspace, isLateral = false } = props ?? {};

  const { canDeleteNode, toggleIsSettingsShown } = useFlowContext();

  useEffect(() => {
    console.debug('WorkspaceFlow(', props, ')');
  }, []);

  const edgeUpdateSuccessful = useRef(true);
  const [nodes, setNodes, onNodesChange] = useNodesState(
    (workspace?.nodes ?? []) as Node[],
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    (workspace?.edges ?? []) as Edge[],
  );

  useEffect(() => {
    console.debug('[WorkspaceFlow] debug:', { workspace, nodes, edges });
  }, [workspace, nodes, edges]);

  const onConnect = useCallback(
    (params: Edge<any> | Connection) => setEdges((els) => addEdge(params, els)),
    [setEdges],
  );

  const onEdgeUpdateStart = useCallback(() => {
    edgeUpdateSuccessful.current = false;
  }, []);

  const onEdgeUpdate = useCallback(
    (oldEdge: Edge<any>, newConnection: Connection) => {
      edgeUpdateSuccessful.current = true;
      setEdges((els) => updateEdge(oldEdge, newConnection, els));
    },
    [setEdges],
  );

  const onEdgeUpdateEnd = useCallback(
    (_: any, edge: { id: string }) => {
      if (!edgeUpdateSuccessful.current) {
        setEdges((eds) => eds.filter((e) => e.id !== edge.id));
      }
      edgeUpdateSuccessful.current = true;
    },
    [setEdges],
  );

  const [status, setStatus] = useState<RunStatus | null>(null);
  // const [statusMessage, setStatusMessage] = useState<string>('');

  useEffect(() => {
    async function getWorkflowStatus() {
      try {
        const response = await fetch('/api/getCurrentWorkflowStatus');
        const responseJson = await response.json();
        // toast.info(`Workflow Status: ${responseJson.status}`);
        setStatus(responseJson.status);
        console.log(responseJson.status);
      } catch (error) {
        // toast.error(`Workflow Status: Error Occured`);
        setStatus('ERROR');
        // setStatusMessage(`error getting workflow status: ${error.message}`);
        console.error('error occured when trying to get workflow status.');
      }
    }
    getWorkflowStatus();
  }, []);

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgeUpdate={onEdgeUpdate}
        onEdgeUpdateStart={onEdgeUpdateStart}
        onEdgeUpdateEnd={onEdgeUpdateEnd}
        fitView
        fitViewOptions={fitViewOptions}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        proOptions={proOptions}
        defaultEdgeOptions={defaultEdgeOptions}
        deleteKeyCode={canDeleteNode ? 'Backspace' : null}
        // onInit={onInit}
      >
        <FlowInterfaces
          workspaceUuid={workspaceUuid}
          workspace={workspace}
          isLateral={isLateral}
        />
      </ReactFlow>
    </>
  );
}

export default WorkspaceFlow;
